import { type FC, useState, useEffect, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui';
import { Input } from '@/components/ui';
import { Label } from '@/components/ui';
import { Button } from '@/components/ui';
import { supabase } from '@/lib/api/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { CheckCircle, AlertCircle } from 'lucide-react';

/**
 * Reset password page - allows users to set a new password after clicking the reset link
 * This page handles the password recovery flow from Supabase
 */
export const ResetPasswordPage: FC = () => {
  const navigate = useNavigate();
  const { needsPasswordReset, clearPasswordResetFlag } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [validSession, setValidSession] = useState<boolean | null>(null);

  // Derive valid session from shared auth state instead of duplicate subscription
  useEffect(() => {
    if (success) return; // don't re-evaluate once password has been updated
    if (needsPasswordReset) {
      setValidSession(true);
    } else {
      // Also check URL hash for recovery tokens (Supabase adds these)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const type = hashParams.get('type');
      setValidSession(!!(accessToken && type === 'recovery'));
    }
  }, [needsPasswordReset, success]);

  // Redirect to dashboard after successful password update
  useEffect(() => {
    if (!success) return;
    const id = setTimeout(() => navigate('/dashboard'), 3000);
    return () => clearTimeout(id);
  }, [success, navigate]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setFormError(null);

    // Validation
    if (!password || !confirmPassword) {
      setFormError('Please fill in all fields');
      return;
    }

    if (password.length < 8) {
      setFormError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        throw error;
      }

      setSuccess(true);
      clearPasswordResetFlag();
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : 'Failed to update password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Loading state while checking session
  if (validSession === null) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-md mx-auto">
            <Card className="bg-white border-turquoise-200 shadow-md">
              <CardContent className="py-8">
                <div className="text-center text-turquoise-700">
                  Verifying reset link...
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Invalid or expired link
  if (!validSession) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-md mx-auto">
            <Card className="bg-white border-turquoise-200 shadow-md">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <AlertCircle className="h-16 w-16 text-red-500" />
                </div>
                <CardTitle className="text-3xl font-viking text-turquoise-900">Invalid or Expired Link</CardTitle>
                <CardDescription className="text-turquoise-700">
                  This password reset link is invalid or has expired
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-turquoise-700 text-center">
                  Password reset links are only valid for a limited time. Please request a new one.
                </p>
                <div className="flex flex-col gap-2 pt-4">
                  <Link to="/forgot-password" className="w-full">
                    <Button className="w-full">
                      Request New Reset Link
                    </Button>
                  </Link>
                  <Link to="/login" className="w-full">
                    <Button variant="ghost" className="w-full">
                      Back to Sign In
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-md mx-auto">
            <Card className="bg-white border-turquoise-200 shadow-md">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <CheckCircle className="h-16 w-16 text-green-500" />
                </div>
                <CardTitle className="text-3xl font-viking text-turquoise-900">Password Updated</CardTitle>
                <CardDescription className="text-turquoise-700">
                  Your password has been successfully reset
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-turquoise-700 text-center">
                  You will be redirected to your dashboard in a few seconds...
                </p>
                <div className="pt-4">
                  <Link to="/dashboard" className="w-full">
                    <Button className="w-full">
                      Go to Dashboard
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-md mx-auto">
          <Card className="bg-white border-turquoise-200 shadow-md">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl font-viking text-turquoise-900">Reset Password</CardTitle>
              <CardDescription className="text-turquoise-700">
                Enter your new password below
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Error message */}
                {formError && (
                  <div className="bg-red-900/40 text-red-200 text-sm p-3 rounded-md border border-red-500/40">
                    {formError}
                  </div>
                )}

                {/* Password field */}
                <div className="space-y-2">
                  <Label htmlFor="password" required className="text-turquoise-900">
                    New Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="new-password"
                    required
                  />
                  <p className="text-xs text-turquoise-600">Must be at least 8 characters long</p>
                </div>

                {/* Confirm password field */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" required className="text-turquoise-900">
                    Confirm New Password
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="new-password"
                    required
                  />
                </div>

                {/* Submit button */}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Updating...' : 'Update Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
